import { useState } from 'react';
import { useMessages } from './i18n/context';
import { blockTypeName } from './blocks/blockTypeName';
import type { AccessLevel, ChildListingResponse, PlanResponse } from './api/types';
import { TODO_LIST_TYPE } from './blocks/todo';
import { TodoList, type TodoCallbacks } from './blocks/TodoList';
import { ChildPlans } from './ChildPlans';
import { ComponentNode, type NodeCallbacks } from './ComponentNode';
import { canEdit, PlanHeader } from './PlanHeader';
import { PlanProgress } from './PlanProgress';
import { ReadOnlyTree } from './ReadOnlyTree';

const BLOCK_TYPES = ['todo-list', 'task', 'note'] as const;

/**
 * The open plan: its title bar, progress, component tree and add-block form (editable at ≥ Write,
 * plain content below — ADR-027 §7), and its sub-planlets. A top-level `todo-list` renders as
 * the checklist block (ADR-029 §3), every other block as a generic node. Purely presentational
 * over the latest server document — every action flows through the API upstairs.
 */
export function PlanEditor({
  plan,
  name,
  access,
  busy,
  childPlans,
  onAddTopLevel,
  onRename,
  onShare,
  onMove,
  onDelete,
  onImport,
  onClose,
  onOpenChild,
  onCreateChild,
  callbacks,
  todo,
}: {
  plan: PlanResponse;
  name: string | null;
  access: AccessLevel;
  busy: boolean;
  childPlans: readonly ChildListingResponse[] | null;
  onAddTopLevel: (type: string, title: string) => void;
  onRename: (name: string) => void;
  onShare: () => void;
  onMove: () => void;
  onDelete: () => void;
  onImport: () => void;
  onClose: () => void;
  onOpenChild: (planId: string, name: string | null) => void;
  onCreateChild: (name: string) => void;
  callbacks: NodeCallbacks;
  todo: TodoCallbacks;
}) {
  const t = useMessages();
  const [type, setType] = useState<string>(BLOCK_TYPES[0]);
  const [title, setTitle] = useState('');
  const trimmed = title.trim();
  const editable = canEdit(access);

  return (
    <section className="plan-editor">
      <PlanHeader
        planId={plan.id}
        name={name}
        version={plan.version}
        access={access}
        busy={busy}
        onRename={onRename}
        onShare={onShare}
        onMove={onMove}
        onDelete={onDelete}
        onImport={onImport}
        onClose={onClose}
      />

      <PlanProgress components={plan.children} />

      {plan.children.length > 0 && !editable && <ReadOnlyTree components={plan.children} />}
      {plan.children.length > 0 && editable && (
        <ul className="block-tree">
          {plan.children.map((component, index) =>
            component.type === TODO_LIST_TYPE ? (
              <TodoList
                key={component.id}
                list={component}
                busy={busy}
                editable
                index={index}
                siblingCount={plan.children.length}
                callbacks={callbacks}
                todo={todo}
              />
            ) : (
              <ComponentNode
                key={component.id}
                component={component}
                index={index}
                siblingCount={plan.children.length}
                busy={busy}
                callbacks={callbacks}
              />
            ),
          )}
        </ul>
      )}

      {editable && (
        <form
          className="block-toolbar add-form card"
          aria-label={t.editor.addBlock}
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed !== '') {
              onAddTopLevel(type, trimmed);
              setTitle('');
            }
          }}
        >
          <label className="field">
            <span>{t.editor.blockType}</span>
            <select aria-label={t.editor.blockType} value={type} onChange={(e) => setType(e.target.value)}>
              {BLOCK_TYPES.map((block) => (
                <option key={block} value={block}>
                  {blockTypeName(t, block)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t.editor.blockTitle}</span>
            <input
              aria-label={t.editor.blockTitle}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.editor.titlePlaceholder}
              disabled={busy}
            />
          </label>
          <button className="button-primary" type="submit" disabled={busy || trimmed === ''}>
            {t.editor.addBlock}
          </button>
        </form>
      )}

      {plan.children.length === 0 && (
        <p className="empty-state">{editable ? t.editor.emptyEditable : t.editor.emptyReadOnly}</p>
      )}

      <ChildPlans
        items={childPlans}
        busy={busy}
        canCreate={editable}
        onOpen={onOpenChild}
        onCreate={onCreateChild}
      />
    </section>
  );
}
